import api from '../../utils/api';

export interface AmbulanceQueueRow {
  bookingId: string;
  userId: string | null;
  userName: string | null;
  userPhone: string | null;
  patientName: string;
  patientPhone: string;
  bookingSource?: 'APP' | 'MASTER_ADMIN' | string;
  customAgencyName?: string | null;
  agencyDisplayName?: string | null;
  dispatchMode: string;
  pickupTimeType: string;
  scheduledPickupAt: string | null;
  pickupAddress: string;
  pickupLocation?: { lat: number; lng: number } | null;
  dropAddress: string;
  dropLocation?: { lat: number; lng: number } | null;
  routeStops?: Array<{ label?: string; address: string; lat?: number; lng?: number }> | null;
  bookingInvites: Array<{
    inviteId: string;
    vendorId: string;
    vendorName: string;
    status: string;
    mode: string;
  }>;
  ambulanceType: string;
  bookingStatus: string;
  paymentStatus: string;
  paymentCollectionType?: 'FULL' | 'ADVANCE' | string | null;
  advancePaidAmount?: number;
  remainingAmount?: number;
  totalPayableAmount: number;
  queueStatus: 'pending' | 'assigned' | 'cancelled';
  vendorsNotified: number;
  requestedVendorNames: string[];
  requestedVendors: Array<{
    inviteId: string | null;
    vendorId: string | null;
    name: string;
    kind: string;
    inviteStatus: string | null;
    inviteMode: string | null;
  }>;
  reminderSentCount: number;
  activeReminderCount: number;
  acceptedByVendorId: string | null;
  acceptedByVendorName: string | null;
  assignedDriverId?: string | null;
  assignedDriverName?: string | null;
  assignedDriverPhone?: string | null;
  cancelledBy?: 'USER' | 'AGENCY' | 'SYSTEM' | null;
  cancellationReason?: string | null;
  cancelledAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AmbulanceTrackingPoint {
  id: number;
  bookingId: string;
  driverId: string | null;
  vendorId: string | null;
  latitude: number;
  longitude: number;
  heading: number | null;
  speed: number | null;
  accuracy: number | null;
  recordedAt: string;
}

export interface AmbulanceQueueResponse {
  success: boolean;
  data: {
    queue: AmbulanceQueueRow[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  };
}

export async function getAmbulanceQueue(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'pending' | 'assigned' | 'cancelled' | 'all';
}): Promise<AmbulanceQueueResponse> {
  const { page = 1, limit = 100, search = '', status = 'pending' } = params || {};
  const response = await api.get<AmbulanceQueueResponse>('/master-admin/ambulance-queue', {
    params: { page, limit, search, status },
  });
  return response.data;
}

export async function getAmbulanceCurrentTrackingLocation(bookingId: string) {
  const response = await api.get<{
    success: boolean;
    message: string;
    data: {
      trackingActive: boolean;
      latitude: number | null;
      longitude: number | null;
      heading?: number | null;
      speed?: number | null;
      accuracy?: number | null;
      updatedAt?: string | null;
      driverId?: string | null;
    };
  }>(
    `/master-admin/ambulance-queue/${encodeURIComponent(bookingId)}/tracking/current-location`
  );
  return response.data;
}

export async function getAmbulanceTrackingHistory(
  bookingId: string,
  params?: { page?: number; limit?: number; order?: 'asc' | 'desc' }
) {
  const response = await api.get<{
    success: boolean;
    data: {
      bookingId: string;
      logs: AmbulanceTrackingPoint[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  }>(
    `/master-admin/ambulance-queue/${encodeURIComponent(bookingId)}/tracking/history`,
    {
      params: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 300,
        order: params?.order ?? 'desc',
      },
    }
  );
  return response.data;
}

export async function getAmbulanceQueueBookingById(bookingId: string) {
  const response = await api.get<{
    success: boolean;
    data: AmbulanceQueueRow;
  }>(`/master-admin/ambulance-queue/${encodeURIComponent(bookingId)}`);
  return response.data;
}
